import { FC } from 'react';
import { Stack } from '@mui/material';

import { TripTypeEnum } from '@mopla/data-models';

import { BookingRouteTransportCard } from './BookingRouteTransportCard';
import { BookingRouteWalkIndicator } from './BookingRouteWalkIndicator';
import { RouteStop } from './RouteStop';
import { ILeg, IRouteStop, IWalkLeg } from './types';
import { getRouteStopVariant } from './utils';

/** Type guard to narrow ILeg to IWalkLeg for TypeScript */
const isWalkLeg = (leg: ILeg): leg is IWalkLeg =>
	leg.mode === TripTypeEnum.WALK || leg.mode === TripTypeEnum.TRANSFER_WALK;

interface IBookingRouteMultiProps {
	stops: IRouteStop[];
	/** Marks every stop time as an estimate (delay of unknown length). */
	isApproximate?: boolean;
}

/**
 * Multi-segment route view for KRK/LBODT/LBT.
 * Shows all stops with transport segment cards between them.
 */
export const BookingRouteMulti: FC<IBookingRouteMultiProps> = ({
	stops,
	isApproximate,
}) => {
	return (
		<Stack>
			{stops.map((stop, stopIdx) => {
				const isLast = stopIdx === stops.length - 1;
				const { legAfter } = stop;

				return (
					<RouteStop
						key={stopIdx}
						isApproximate={isApproximate}
						variant={getRouteStopVariant(stopIdx, stops.length)}
						time={stop.time}
						effectiveTime={stop.effectiveTime}
						address={stop.address}
						lat={stop.lat}
						lng={stop.lng}
						dashedConnector={legAfter && isWalkLeg(legAfter)}
						addressTestId={stop.addressTestId}
					>
						{/* Leg after this stop */}
						{!isLast &&
							legAfter &&
							(isWalkLeg(legAfter) ? (
								<BookingRouteWalkIndicator
									durationMinutes={legAfter.durationMinutes}
									distanceMeters={legAfter.distanceMeters}
									mode={legAfter.mode}
								/>
							) : (
								<BookingRouteTransportCard
									mode={legAfter.mode}
									vehicleType={legAfter.vehicleType}
									lineNumber={legAfter.lineNumber}
									title={legAfter.lineName}
									duration={legAfter.duration}
									price={legAfter.price}
									intermediateStops={legAfter.intermediateStops}
								/>
							))}
					</RouteStop>
				);
			})}
		</Stack>
	);
};
